import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Empty,
  Pagination,
  Progress,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { GlobalToken } from 'antd/es/theme/interface';
import { ITableColumnType } from '../../../../../core/interfaces';
import { RecommendationCandidate } from '../models/job.model';
import { TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import {
  EyeOutlined,
  MailOutlined,
  ReloadOutlined,
  RobotOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { route } from '../../../../../constants/routes';
import {
  RecommendationFitBand,
  formatSkillOverlap,
  recommendationFitBand,
  recommendationFitLabel,
  recommendationFitProgressToken,
  recommendationFitTagColor,
  resolveRecommendationFitScore,
} from '../utils/recommendation-fit-labels';

const DEFAULT_SORT_FIELD = 'relevance';
const DEFAULT_SORT_ORDER = 'descend' as const;
const MOBILE_TABLE_BREAKPOINT = 768;

function progressStrokeColor(
  band: RecommendationFitBand,
  token: GlobalToken
): string {
  return token[recommendationFitProgressToken[band]];
}

function formatDisplayScore(score: number): string {
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

interface SkillFitCellProps {
  candidate: RecommendationCandidate;
  token: GlobalToken;
  compact?: boolean;
}

function SkillFitCell({
  candidate,
  token,
  compact = false,
}: SkillFitCellProps) {
  const score = resolveRecommendationFitScore(
    candidate.relevance,
    candidate.matchedSkillCount,
    candidate.totalJobSkills,
    candidate.aiBriefScore
  );
  const band = recommendationFitBand(score);
  const overlap = formatSkillOverlap(
    candidate.matchedSkillCount,
    candidate.totalJobSkills
  );

  if (score == null || !band) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }

  const strokeColor = progressStrokeColor(band, token);
  const tooltipTitle = candidate.aiBriefScore
    ? 'Weighted resume evidence from the latest AI Brief: Met 100%, Partial 65%, Unclear 35%, Not met 0%'
    : overlap
      ? `${overlap} matched · Preliminary profile overlap until an AI Brief is available`
      : 'Preliminary profile skill overlap for this job';

  return (
    <Tooltip title={tooltipTitle}>
      <div className={compact ? 'w-full' : 'min-w-[168px] max-w-[220px]'}>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`font-semibold tabular-nums leading-none ${compact ? 'text-base' : 'text-lg'}`}
            style={{ color: strokeColor }}
          >
            {formatDisplayScore(score)}%
          </span>
          <Tag
            color={recommendationFitTagColor[band]}
            className="m-0 text-xs leading-5"
          >
            {recommendationFitLabel[band]}
          </Tag>
        </div>
        <span className="mt-1 flex items-center gap-1 text-[11px] font-medium text-gray-500">
          {candidate.aiBriefScore ? <RobotOutlined aria-hidden /> : null}
          {candidate.aiBriefScore ? 'AI Brief score' : 'Profile overlap'}
        </span>
        <div className="mt-1.5 flex items-center gap-2">
          <Progress
            percent={score}
            size="small"
            showInfo={false}
            strokeColor={strokeColor}
            className="mb-0 min-w-0 flex-1 [&_.ant-progress-inner]:align-middle"
            aria-hidden
          />
          {overlap && !candidate.aiBriefScore ? (
            <Typography.Text
              type="secondary"
              className="shrink-0 text-xs tabular-nums"
            >
              {candidate.matchedSkillCount}/{candidate.totalJobSkills}
            </Typography.Text>
          ) : null}
        </div>
      </div>
    </Tooltip>
  );
}

function ContactActions({ candidate }: { candidate: RecommendationCandidate }) {
  return (
    <div className="inline-flex items-center gap-2">
      {candidate.email ? (
        <Tooltip title="Send email">
          <Button
            type="default"
            size="small"
            href={`mailto:${candidate.email}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Email ${candidate.firstname} ${candidate.lastname}`}
            icon={<MailOutlined className="text-blue-500" />}
            className="!h-8 !w-8 !rounded-lg !border-blue-100 !bg-blue-50"
          />
        </Tooltip>
      ) : null}
      {candidate.phoneNumber ? (
        <Tooltip title="Send WhatsApp">
          <Button
            type="default"
            size="small"
            href={`https://wa.me/${candidate.phoneNumber.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`WhatsApp ${candidate.firstname} ${candidate.lastname}`}
            icon={<WhatsAppOutlined className="text-green-600" />}
            className="!h-8 !w-8 !rounded-lg !border-green-100 !bg-green-50"
          />
        </Tooltip>
      ) : null}
    </div>
  );
}

interface RecommendationCardProps {
  candidate: RecommendationCandidate;
  token: GlobalToken;
  onViewProfile: (candidate: RecommendationCandidate) => void;
}

function RecommendationCard({
  candidate,
  token,
  onViewProfile,
}: RecommendationCardProps) {
  return (
    <article className="rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-gray-900">
            {candidate.firstname} {candidate.lastname}
          </p>
          <div className="mt-2">
            <SkillFitCell candidate={candidate} token={token} compact />
          </div>
        </div>
        <Button
          type="primary"
          ghost
          size="small"
          aria-label={`View profile for ${candidate.firstname} ${candidate.lastname}`}
          icon={<EyeOutlined />}
          onClick={() => onViewProfile(candidate)}
          className="shrink-0"
        />
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
        <Typography.Text type="secondary" className="text-xs">
          Contact
        </Typography.Text>
        <ContactActions candidate={candidate} />
      </div>
    </article>
  );
}

export interface RecommendationsTableProps {
  data: RecommendationCandidate[];
  total: number;
  jobName?: string;
  jobSkillCount?: number;
  loading?: boolean;
  loaded?: boolean;
  errorMessage?: string | null;
  onRetry?: () => void;
  hasActiveFilter?: boolean;
  sortField?: string;
  sortOrder?: 'ascend' | 'descend' | null;
  pagination: { current: number; pageSize: number };
  onChange: (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<RecommendationCandidate>
      | SorterResult<RecommendationCandidate>[]
  ) => void;
}

const RecommendationsTable: React.FC<RecommendationsTableProps> = ({
  data,
  total,
  jobName,
  jobSkillCount = 0,
  loading = false,
  loaded = false,
  errorMessage = null,
  onRetry,
  hasActiveFilter = false,
  sortField = DEFAULT_SORT_FIELD,
  sortOrder = DEFAULT_SORT_ORDER,
  pagination,
  onChange,
}) => {
  const navigate = useNavigate();
  const { id: jobId } = useParams<{ id?: string }>();
  const { token } = theme.useToken();
  const [isMobileView, setIsMobileView] = useState(
    window.innerWidth < MOBILE_TABLE_BREAKPOINT
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < MOBILE_TABLE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePagination = useCallback(
    (page: number, pageSize: number) => {
      onChange(
        { current: page, pageSize },
        {},
        {} as SorterResult<RecommendationCandidate>
      );
    },
    [onChange]
  );

  const viewProfile = useCallback(
    (candidate: RecommendationCandidate) => {
      navigate(route(`/candidate-profile/${candidate.candidateId}`), {
        state: {
          candidateName: `${candidate.firstname} ${candidate.lastname}`,
          fromRecommendations: true,
          jobId,
          jobName,
        },
      });
    },
    [navigate, jobId, jobName]
  );

  const columns: ITableColumnType<RecommendationCandidate>[] = useMemo(
    () => [
      {
        title: 'Fit score',
        key: 'skillFit',
        dataIndex: 'relevance',
        width: 230,
        align: 'left',
        sorter: true,
        defaultSortOrder: 'descend',
        sortOrder: sortField === 'relevance' ? sortOrder : null,
        render: (_, candidate) => (
          <SkillFitCell candidate={candidate} token={token} />
        ),
      },
      {
        title: 'Candidate',
        dataIndex: 'firstname',
        key: 'candidate',
        align: 'left',
        filter: true,
        sorter: true,
        ellipsis: true,
        sortOrder: sortField === 'firstname' ? sortOrder : null,
        render: (_: unknown, candidate: RecommendationCandidate) => (
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sm font-semibold text-sky-800">
              {(candidate.firstname?.[0] || '') +
                (candidate.lastname?.[0] || '')}
            </span>
            <div className="min-w-0">
              <strong className="block truncate text-sm text-gray-900">
                {candidate.firstname} {candidate.lastname}
              </strong>
              <span className="block truncate text-xs text-gray-500">
                {candidate.email || 'No email available'}
              </span>
            </div>
          </div>
        ),
      },
      {
        title: 'Contact',
        key: 'contact',
        width: 110,
        align: 'center',
        render: (candidate: RecommendationCandidate) => (
          <ContactActions candidate={candidate} />
        ),
      },
      {
        title: 'Next step',
        key: 'actions',
        width: 150,
        align: 'center',
        render: (candidate: RecommendationCandidate) => (
          <Button
            type="primary"
            ghost
            icon={<EyeOutlined />}
            onClick={() => viewProfile(candidate)}
            className="!rounded-lg"
          >
            View profile
          </Button>
        ),
      },
    ],
    [sortField, sortOrder, token, viewProfile]
  );

  if (loading && !loaded) {
    return (
      <div
        className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/80 py-12"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <Spin size="large" />
        <Typography.Text type="secondary" className="mt-4">
          Loading skill-matched candidates…
        </Typography.Text>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <Alert
        type="error"
        showIcon
        message="Recommendations unavailable"
        description={errorMessage}
        action={
          onRetry ? (
            <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
              Retry
            </Button>
          ) : undefined
        }
      />
    );
  }

  if (loaded && total === 0) {
    const emptyDescription =
      jobSkillCount === 0
        ? 'Add required and nice-to-have skills on the Details tab, then save the job. Recommendations are rebuilt from skill overlap using the same scoring bands as Check fit.'
        : hasActiveFilter
          ? 'No candidates match your filters. Clear the name filters or broaden your search.'
          : 'No candidates in your talent pool meet the skill overlap threshold for this job yet. Add candidates with matching skills or refine job requirements.';

    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div className="mx-auto max-w-md space-y-2 text-left">
            <Typography.Text strong>No recommendations yet</Typography.Text>
            <Typography.Paragraph type="secondary" className="!mb-0">
              {emptyDescription}
            </Typography.Paragraph>
          </div>
        }
      />
    );
  }

  if (isMobileView) {
    return (
      <div className="space-y-3 text-left">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : null}
        {data.map((candidate) => (
          <RecommendationCard
            key={candidate.candidateId}
            candidate={candidate}
            token={token}
            onViewProfile={viewProfile}
          />
        ))}
        <div className="flex justify-center pt-2">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={total}
            showSizeChanger={false}
            simple
            onChange={handlePagination}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table<RecommendationCandidate>
        columns={columns}
        dataSource={data}
        rowKey="candidateId"
        loading={loading}
        size="middle"
        tableLayout="fixed"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
          onChange: handlePagination,
          onShowSizeChange: handlePagination,
        }}
        onChange={onChange}
        scroll={{ x: 640 }}
        className="recommendations-table [&_.ant-table-thead>tr>th]:bg-gray-50/80"
      />
    </div>
  );
};

export default RecommendationsTable;
